// ─── Legal documents — content ──────────────────────────────────────────────
// PROJET rédigé par l'équipe produit — à faire valider par avocat + DPO.
// Contenu factuel (identité, hébergeur…) centralisé dans src/config/legal.ts.
//
// Modèle de contenu volontairement simple : une suite de blocs par section.
// Le rendu (gras via **…**, listes) est assuré par LegalDocumentPage.

import { LEGAL_ENTITY, LEGAL_HOSTING, LEGAL_VERSION, type LegalSlug } from '../../config/legal'

export type LegalBlock =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'sub'; text: string }

export interface LegalSection {
  heading: string
  blocks: LegalBlock[]
}

export interface LegalDocContent {
  slug: LegalSlug
  title: string
  effectiveDate: string
  intro: string
  sections: LegalSection[]
}

const p = (text: string): LegalBlock => ({ kind: 'p', text })
const list = (items: string[]): LegalBlock => ({ kind: 'list', items })

const E = LEGAL_ENTITY
const H = LEGAL_HOSTING

// ─── Mentions légales ───────────────────────────────────────────────────────

const mentionsLegales: LegalDocContent = {
  slug: 'mentions-legales',
  title: 'Mentions légales',
  effectiveDate: LEGAL_VERSION,
  intro:
    "Conformément à la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique (LCEN), les présentes mentions légales identifient l'éditeur et l'hébergeur du service Careflai.",
  sections: [
    {
      heading: 'Éditeur du service',
      blocks: [
        list([
          `Dénomination sociale : **${E.legalName}** (nom commercial : ${E.tradeName})`,
          `Forme juridique : ${E.legalForm}`,
          `Capital social : ${E.capital}`,
          `Siège social : ${E.address}`,
          `RCS ${E.rcsCity} : ${E.rcsNumber} — SIREN : ${E.siren} — SIRET : ${E.siret}`,
          `N° de TVA intracommunautaire : ${E.vatNumber}`,
          `Directeur de la publication : ${E.publicationDirector}`,
          `Contact : ${E.contactEmail}`,
        ]),
      ],
    },
    {
      heading: 'Hébergement',
      blocks: [
        p("**Hébergeur de la plateforme :**"),
        list([`${H.appHost}`, `${H.appHostAddress}`, `Bureau d'enregistrement du nom de domaine : ${H.registrar}`]),
        p(
          "**Hébergement des données de santé à caractère personnel :** conformément à l'article L. 1111-8 du Code de la santé publique, les données de santé sont hébergées auprès d'un hébergeur certifié « Hébergeur de Données de Santé » (HDS) :"
        ),
        list([`${H.hdsHost}`, `Certification HDS : ${H.hdsCertification}`]),
      ],
    },
    {
      heading: 'Propriété intellectuelle',
      blocks: [
        p(
          `L'ensemble des éléments de la plateforme Careflai (marques, logos, interfaces, code, modèles d'intelligence artificielle, bases de données) est protégé par le droit de la propriété intellectuelle et demeure la propriété exclusive de ${E.legalName} ou de ses partenaires. Toute reproduction ou représentation, totale ou partielle, sans autorisation écrite préalable est interdite.`
        ),
      ],
    },
    {
      heading: 'Signalement — matériovigilance',
      blocks: [
        p(
          `Tout dysfonctionnement susceptible d'avoir une incidence sur un résultat d'analyse peut être signalé à ${E.supportEmail}. Le cas échéant, les obligations de matériovigilance / réactovigilance applicables aux dispositifs médicaux de diagnostic in vitro sont mises en œuvre auprès de l'ANSM.`
        ),
      ],
    },
  ],
}

// ─── CGU ────────────────────────────────────────────────────────────────────

const cgu: LegalDocContent = {
  slug: 'cgu',
  title: "Conditions Générales d'Utilisation (CGU)",
  effectiveDate: LEGAL_VERSION,
  intro:
    "Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'usage de la plateforme Careflai. En créant un compte ou en utilisant le service, l'utilisateur reconnaît en avoir pris connaissance et les accepter sans réserve.",
  sections: [
    {
      heading: '1. Objet',
      blocks: [
        p(
          "Careflai est une plateforme logicielle d'**aide à l'analyse d'images biologiques** (immunoblots / Western Blot, images de biologie médicale) destinée aux professionnels de santé et aux laboratoires. Elle fournit des propositions d'interprétation, des annotations et des outils de traçabilité destinés à assister — et non à remplacer — le jugement du professionnel de santé."
        ),
      ],
    },
    {
      heading: '2. Nature du service — aide à la décision',
      blocks: [
        p(
          "**Careflai ne pose pas de diagnostic.** Les résultats, scores, détections de bandes et interprétations produits par les modèles d'intelligence artificielle sont **indicatifs** et doivent systématiquement être vérifiés et validés par un professionnel de santé habilité."
        ),
        p(
          "La décision clinique finale, sa validation et son éventuelle communication au patient relèvent de la seule responsabilité du professionnel de santé qui l'exploite. L'utilisateur s'engage à exercer son esprit critique et à ne jamais fonder une conclusion clinique sur la seule sortie automatisée de la plateforme."
        ),
        p(
          "Selon son périmètre d'usage, la plateforme peut relever de la réglementation applicable aux dispositifs médicaux de diagnostic in vitro (règlement UE 2017/746 — IVDR). Le statut réglementaire applicable est précisé dans la documentation contractuelle. À défaut de marquage CE couvrant un usage donné, cet usage est réputé **à des fins de recherche ou d'évaluation** et non diagnostique."
        ),
      ],
    },
    {
      heading: '3. Accès et compte utilisateur',
      blocks: [
        p(
          "L'accès à la plateforme requiert la création d'un compte nominatif, réservé aux professionnels habilités. L'utilisateur est responsable de la confidentialité de ses identifiants et de toute action réalisée depuis son compte. Toute utilisation frauduleuse doit être signalée sans délai."
        ),
        p(
          "L'utilisateur s'engage à fournir des informations exactes lors de son inscription et à les maintenir à jour."
        ),
      ],
    },
    {
      heading: '4. Obligations de l’utilisateur',
      blocks: [
        p("L'utilisateur s'engage à :"),
        list([
          "utiliser la plateforme conformément à sa destination et aux règles de l'art de sa profession ;",
          "valider chaque résultat par son expertise avant toute exploitation clinique ;",
          "ne téléverser que des données qu'il est autorisé à traiter et, sauf cadre HDS et base légale explicites, sous forme **pseudonymisée** (sans nom, prénom ni identifiant directement nominatif) ;",
          "respecter le secret professionnel et la réglementation applicable à la protection des données de santé ;",
          "ne pas tenter de contourner les mesures de sécurité, ni de rétro-concevoir les modèles ou le code.",
        ]),
      ],
    },
    {
      heading: '5. Intelligence artificielle — transparence',
      blocks: [
        p(
          "Certaines fonctionnalités reposent sur des systèmes d'intelligence artificielle. Conformément au règlement UE 2024/1689 (« AI Act ») et à l'article L. 4001-3 du Code de la santé publique, l'utilisateur est informé du recours à ces systèmes, de leur caractère d'aide à la décision, et de l'exigence d'une supervision humaine. Une notice dédiée est présentée dans l'application et doit être acquittée."
        ),
      ],
    },
    {
      heading: '6. Disponibilité et évolutions',
      blocks: [
        p(
          "Careflai s'efforce d'assurer la disponibilité du service mais ne garantit pas une accessibilité ininterrompue. Le service peut être suspendu pour maintenance, mis à jour ou faire l'objet d'évolutions fonctionnelles, notamment des modèles d'IA, dans le respect des obligations réglementaires applicables."
        ),
      ],
    },
    {
      heading: '7. Responsabilité',
      blocks: [
        p(
          `${E.legalName} ne saurait être tenue responsable des conséquences d'une décision clinique prise par l'utilisateur, ni d'un usage non conforme à la destination du service ou aux présentes CGU. La responsabilité de l'éditeur est limitée dans les conditions prévues par les CGV et la documentation contractuelle.`
        ),
      ],
    },
    {
      heading: '8. Modification des CGU',
      blocks: [
        p(
          "Les présentes CGU peuvent être modifiées. En cas d'évolution substantielle, l'utilisateur en est informé et une nouvelle acceptation peut être requise pour continuer à utiliser le service."
        ),
      ],
    },
    {
      heading: '9. Droit applicable',
      blocks: [
        p(
          "Les présentes CGU sont régies par le droit français. Tout litige relatif à leur interprétation ou exécution relève, à défaut de résolution amiable, des juridictions françaises compétentes."
        ),
      ],
    },
  ],
}

// ─── CGV ────────────────────────────────────────────────────────────────────

const cgv: LegalDocContent = {
  slug: 'cgv',
  title: 'Conditions Générales de Vente (CGV)',
  effectiveDate: LEGAL_VERSION,
  intro:
    "Les présentes Conditions Générales de Vente (CGV) encadrent la fourniture, à titre onéreux, de la solution logicielle Careflai aux clients professionnels (établissements de santé, laboratoires, structures assimilées). Elles complètent, le cas échéant, le contrat de souscription ou le marché conclu entre les parties.",
  sections: [
    {
      heading: '1. Champ d’application',
      blocks: [
        p(
          "Les présentes CGV s'appliquent à toute souscription à un abonnement Careflai. Elles prévalent sur tout document contradictoire du client, sauf conditions particulières acceptées par écrit par l'éditeur. La souscription emporte acceptation sans réserve des CGV."
        ),
      ],
    },
    {
      heading: '2. Description des offres',
      blocks: [
        p(
          "Careflai est proposée sous forme d'abonnement (SaaS) donnant accès à des modules fonctionnels (analyse d'images, interprétation assistée, collaboration, export…). Le périmètre exact des fonctionnalités, le nombre d'utilisateurs et les éventuelles options sont définis dans l'offre souscrite ou les conditions particulières."
        ),
      ],
    },
    {
      heading: '3. Prix et modalités de paiement',
      blocks: [
        p(
          "Les prix sont indiqués en euros, hors taxes, et précisés dans l'offre applicable. Sauf stipulation contraire, l'abonnement est facturé selon la périodicité convenue (mensuelle ou annuelle). Les factures sont payables à réception ou selon l'échéance convenue."
        ),
        p(
          "Tout retard de paiement peut entraîner l'application de pénalités au taux légal, une indemnité forfaitaire de recouvrement de 40 €, ainsi que la suspension de l'accès après mise en demeure restée sans effet."
        ),
      ],
    },
    {
      heading: '4. Durée, renouvellement et résiliation',
      blocks: [
        p(
          "L'abonnement est conclu pour la durée précisée dans l'offre et se renouvelle par tacite reconduction sauf dénonciation par l'une des parties dans le préavis convenu. Chaque partie peut résilier de plein droit en cas de manquement grave de l'autre non réparé après mise en demeure."
        ),
        p(
          "À l'issue du contrat, le client dispose d'un délai pour récupérer ses données conformément à la politique de réversibilité ; passé ce délai, les données sont supprimées selon la politique de conservation."
        ),
      ],
    },
    {
      heading: '5. Licence d’utilisation',
      blocks: [
        p(
          `${E.legalName} concède au client un droit d'utilisation personnel, non exclusif et non cessible de la plateforme, pour la durée de l'abonnement et dans la limite du périmètre souscrit. Aucun transfert de propriété intellectuelle n'est opéré.`
        ),
      ],
    },
    {
      heading: '6. Niveau de service et support',
      blocks: [
        p(
          "Les engagements de disponibilité, les modalités de support et, le cas échéant, les indicateurs de niveau de service (SLA) sont précisés dans l'offre ou une annexe dédiée. Le support est joignable via les canaux indiqués dans l'application."
        ),
      ],
    },
    {
      heading: '7. Protection des données et sous-traitance',
      blocks: [
        p(
          "Dans le cadre du traitement de données de santé pour le compte du client, les parties concluent un accord de traitement (DPA) conforme à l'article 28 du RGPD, précisant les rôles (responsable de traitement / sous-traitant), les finalités, les durées et les mesures de sécurité, ainsi que l'hébergement certifié HDS. Voir la Politique de confidentialité."
        ),
      ],
    },
    {
      heading: '8. Garantie et limitation de responsabilité',
      blocks: [
        p(
          "La plateforme est une solution d'aide à la décision : elle ne se substitue pas au jugement du professionnel de santé. L'éditeur ne garantit pas l'exactitude d'une interprétation automatisée non validée par l'utilisateur."
        ),
        p(
          "Sauf faute lourde ou dolosive, et dans les limites autorisées par la loi, la responsabilité de l'éditeur au titre du contrat est limitée aux dommages directs et plafonnée au montant des sommes versées par le client au titre des douze (12) mois précédant le fait générateur."
        ),
      ],
    },
    {
      heading: '9. Confidentialité',
      blocks: [
        p(
          "**9.1 Informations confidentielles.** Sont confidentielles toutes les informations, quel qu'en soit le support, échangées ou rendues accessibles dans le cadre du contrat, notamment : la plateforme et ses fonctionnalités, y compris celles en cours d'évaluation ou non publiées ; les algorithmes, modèles, méthodes et résultats de performance ; les documents techniques, commerciaux et financiers ; le contenu des échanges entre les parties, dont les retours d'évaluation et les signalements."
        ),
        p(
          "**9.2 Engagements.** Chaque partie s'engage à ne pas divulguer les informations confidentielles de l'autre à des tiers, à ne les utiliser que pour l'exécution du contrat, et à n'y donner accès qu'aux membres de son personnel qui en ont besoin et sont tenus d'obligations de confidentialité au moins équivalentes. Les données de santé relèvent en outre de la Politique de confidentialité et de l'accord de traitement (article 7)."
        ),
        p(
          "**9.2 bis Résultats d'évaluation et non-concurrence d'usage.** Les résultats produits par ou avec la plateforme — mesures de performance, taux de concordance, comparaisons, captures d'écran, exports, constats d'évaluation — ne peuvent être communiqués à des tiers, notamment aux industriels du diagnostic, fabricants de réactifs, de trousses ou d'automates, et à tout concurrent direct ou indirect de Careflai, ni être utilisés pour concevoir, entraîner, améliorer ou évaluer un produit ou service concurrent, sans l'accord préalable écrit des deux parties. Les publications et communications scientifiques portant sur ces résultats font l'objet d'un accord écrit spécifique entre les parties, qui en fixe le périmètre et les mentions."
        ),
        p(
          "**9.3 Exclusions.** Ne sont pas confidentielles les informations qui sont ou deviennent publiques sans faute de la partie réceptrice, qu'elle connaissait légitimement avant leur communication, qu'elle reçoit d'un tiers autorisé, ou qu'elle doit divulguer en vertu de la loi ou d'une décision de justice — à charge d'en informer l'autre partie sans délai lorsque c'est permis."
        ),
        p(
          "**9.4 Durée.** Ces engagements s'appliquent pendant toute la durée du contrat et survivent cinq (5) ans après sa fin, quelle qu'en soit la cause. Au terme du contrat, chaque partie restitue ou détruit les informations confidentielles de l'autre, sous réserve des obligations légales de conservation."
        ),
      ],
    },
    {
      heading: '10. Droit applicable et juridiction',
      blocks: [
        p(
          "Les présentes CGV sont soumises au droit français. En cas de litige, les parties rechercheront une solution amiable avant de saisir les juridictions françaises compétentes. Pour les marchés publics, les règles de la commande publique applicables prévalent."
        ),
      ],
    },
  ],
}

// ─── Politique de confidentialité ───────────────────────────────────────────

const confidentialite: LegalDocContent = {
  slug: 'confidentialite',
  title: 'Politique de confidentialité & protection des données',
  effectiveDate: LEGAL_VERSION,
  intro:
    "La présente politique décrit comment Careflai traite les données à caractère personnel, y compris les données de santé, conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi Informatique et Libertés.",
  sections: [
    {
      heading: '1. Responsable de traitement et sous-traitance',
      blocks: [
        p(
          `Pour les données de santé de ses patients, l'établissement client agit en qualité de **responsable de traitement**. ${E.legalName} agit en qualité de **sous-traitant** au sens de l'article 28 du RGPD, dans le cadre d'un accord de traitement des données (DPA). Pour les données de gestion des comptes utilisateurs, Careflai agit en tant que responsable de traitement.`
        ),
      ],
    },
    {
      heading: '2. Données traitées',
      blocks: [
        list([
          "**Données de compte** : identité professionnelle, e-mail, téléphone, spécialité, organisation, journaux de connexion ;",
          "**Données d'examen** : images biologiques téléversées, annotations, interprétations, métadonnées d'analyse ;",
          "**Données de santé** (catégorie particulière — art. 9 RGPD) lorsque les images ou métadonnées s'y rapportent.",
        ]),
        p(
          "Sauf cadre HDS et base légale explicites, l'utilisateur est invité à ne téléverser que des données **pseudonymisées**."
        ),
      ],
    },
    {
      heading: '3. Finalités et bases légales',
      blocks: [
        list([
          "Fourniture du service d'aide à l'analyse — exécution du contrat / mission d'intérêt public de soin ;",
          "Sécurité, traçabilité des accès et support technique — intérêt légitime / obligation légale ;",
          "Amélioration des modèles — sur données pseudonymisées ou anonymisées, dans le cadre défini contractuellement et, le cas échéant, avec les garanties requises ;",
          "Respect des obligations réglementaires (vigilance, comptabilité) — obligation légale.",
        ]),
      ],
    },
    {
      heading: '4. Hébergement des données de santé (HDS)',
      blocks: [
        p(
          `Les données de santé à caractère personnel sont hébergées auprès d'un hébergeur certifié HDS (${H.hdsHost}), conformément à l'article L. 1111-8 du Code de la santé publique. Les données sont hébergées au sein de l'Union européenne.`
        ),
      ],
    },
    {
      heading: '5. Destinataires et accès de support',
      blocks: [
        p(
          "Les données ne sont accessibles qu'aux personnes habilitées de l'établissement client et, pour les besoins de support, de débogage et d'amélioration, à l'équipe Careflai. **Chaque accès de support est tracé** et consultable par l'utilisateur (Mon profil → Confidentialité)."
        ),
        p(
          "Aucune donnée n'est vendue. Les éventuels sous-traitants ultérieurs (hébergement, e-mail…) sont soumis à des engagements contractuels équivalents."
        ),
      ],
    },
    {
      heading: '6. Durées de conservation',
      blocks: [
        p(
          "Les données sont conservées pour la durée nécessaire aux finalités, puis archivées ou supprimées conformément aux durées légales applicables (notamment celles du dossier de biologie médicale) et à la politique de conservation convenue avec l'établissement."
        ),
      ],
    },
    {
      heading: '7. Sécurité',
      blocks: [
        p(
          "Careflai met en œuvre des mesures techniques et organisationnelles appropriées, en cohérence avec le référentiel HDS et l'état de l'art :"
        ),
        list([
          "**Chiffrement** : TLS 1.3 pour les données en transit, AES-256 pour les données au repos ;",
          "**Résidence des données** dans l'Union européenne par défaut ;",
          "**Cloisonnement** (isolation des locataires) aux niveaux stockage, calcul et journalisation ;",
          "contrôle des accès, journalisation des accès de support, sauvegardes et gestion des habilitations ;",
          "démarche de certification **ISO/IEC 27001 en cours**.",
        ]),
      ],
    },
    {
      heading: '8. Droits des personnes',
      blocks: [
        p(
          "Les personnes concernées disposent des droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité. Ces droits s'exercent en principe auprès de l'établissement de santé responsable de traitement ; Careflai apporte son assistance en qualité de sous-traitant."
        ),
        p(`Contact du délégué à la protection des données (DPO) de Careflai : ${E.dpoEmail}.`),
        p(
          "Toute personne peut introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL)."
        ),
      ],
    },
    {
      heading: '9. Cookies',
      blocks: [
        p(
          "La plateforme n'utilise **aucun cookie de traçage** ni de mesure d'audience. Seuls des cookies techniques strictement nécessaires au fonctionnement et à la sécurité du service sont déposés ; ils ne requièrent pas de consentement préalable. Si un traceur non essentiel devait être introduit, il serait soumis au recueil préalable du consentement conformément aux recommandations de la CNIL."
        ),
      ],
    },
    {
      heading: '10. Modifications',
      blocks: [
        p(
          "La présente politique peut être mise à jour pour tenir compte des évolutions légales ou du service. La version applicable est datée ci-dessus."
        ),
      ],
    },
  ],
}

export const LEGAL_CONTENT: Record<LegalSlug, LegalDocContent> = {
  cgu,
  cgv,
  'mentions-legales': mentionsLegales,
  confidentialite,
}
